import { actionCreator } from '@ardoq/rxbeach';
import { PropertyValue } from 'aqTypes';
import {
  APIComponentAttributes,
  APIEntityType,
  APIReferenceAttributes,
  APITagAttributes,
  ArdoqId,
} from '@ardoq/api-types';

export type UploadStylesTabAttachmentPayload = {
  file: File;
  orgId: ArdoqId;
  component: APIComponentAttributes;
};

export const uploadStylesTabAttachment =
  actionCreator<UploadStylesTabAttachmentPayload>(
    '[ComponentOverview2025] UPLOAD_STYLES_TAB_ATTACHMENT'
  );
import {
  EditableField,
  ComponentOverviewTab,
  FieldScopeSource,
  FieldsSortBy,
} from './types';
import { BatchResult } from '@ardoq/api';
import { EditableDefaultFields } from './consts';
import { Trackable } from 'tracking/trackPayload';
import { TrackingLocation } from '@ardoq/data-model';

export const setCurrentTab = actionCreator<string>(
  '[ComponentOverview2025] SET_CURRENT_TAB'
);

export type SetActiveEditableFieldPayload = {
  fieldName: string;
  entityId: string;
  entityType: APIEntityType;
  entity: APIComponentAttributes | APIReferenceAttributes;
  scopeSource: FieldScopeSource;
};
export const setActiveEditableField =
  actionCreator<SetActiveEditableFieldPayload | null>(
    '[ComponentOverview2025] SET_ACTIVE_EDITABLE_FIELD'
  );

export const setCurrentEditableFieldValue = actionCreator<PropertyValue>(
  '[ComponentOverview2025] SET_CURRENT_EDITABLE_FIELD_VALUE'
);

export const setCurrentTags = actionCreator<string[]>(
  '[ComponentOverview2025] SET_CURRENT_TAGS'
);

// AI produced a description: sets the value AND marks provenance 'untouched'.
// Distinct from setCurrentEditableFieldValue (a user edit) so the two can be
// told apart for the acceptance metric.
export const applyGeneratedDescription = actionCreator<string>(
  '[ComponentOverview2025] APPLY_GENERATED_DESCRIPTION'
);

export type SaveFieldValuePayload = {
  value: PropertyValue;
  entity: APIComponentAttributes | APIReferenceAttributes;
  fieldName: string;
  scopeSource: FieldScopeSource;
};
export const saveFieldValue = actionCreator<SaveFieldValuePayload>(
  '[ComponentOverview2025] SAVE_FIELD_VALUE'
);

// Identifies the field to auto-save by entity reference rather than a snapshot
// of the entity, so the routine can look up the freshest entity at save time.
// A stale snapshot would revert other fields saved in between.
export type AutoSaveFieldValuePayload = {
  value: PropertyValue;
  fieldName: string;
  entityId: ArdoqId;
  entityType: APIEntityType;
  scopeSource: FieldScopeSource;
};

// Persists a field's unsaved changes when the user moves on to edit another
// field, without touching the now-active editor's status or closing it.
export const autoSaveFieldValue = actionCreator<AutoSaveFieldValuePayload>(
  '[ComponentOverview2025] AUTO_SAVE_FIELD_VALUE'
);

export const setEditableFieldStatus = actionCreator<EditableField['status']>(
  '[ComponentOverview2025] SET_EDITABLE_FIELD_STATUS'
);

export const fieldValueUpdateSuccess = actionCreator<BatchResult>(
  '[ComponentOverview2025] FIELD_VALUE_UPDATE_SUCCESS'
);

export const componentsUpdated = actionCreator<APIComponentAttributes[]>(
  '[ComponentOverview2025] COMPONENTS_UPDATED'
);

export const toggleFieldDescriptions = actionCreator<void>(
  '[ComponentOverview2025] TOGGLE_FIELD_DESCRIPTIONS'
);

export type EditDefaultFieldPayload = {
  fieldName: EditableDefaultFields;
  entity: APIComponentAttributes | APIReferenceAttributes;
  scopeSource: FieldScopeSource;
};

export const editDefaultField = actionCreator<EditDefaultFieldPayload>(
  '[ComponentOverview2025] EDIT_DEFAULT_FIELD'
);

export type EditTagsPayload = {
  entityId: ArdoqId;
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE;
};

export const editTags = actionCreator<EditTagsPayload>(
  '[ComponentOverview2025] EDIT_TAGS'
);

export type SaveTagsPayload = {
  currentTags: APITagAttributes[];
  newTags: APITagAttributes[];
};

export const saveTags = actionCreator<SaveTagsPayload>(
  '[ComponentOverview2025] SAVE_TAGS'
);

export type SaveTagsSuccessPayload = {
  create: APITagAttributes[];
  update: APITagAttributes[];
  deleted: ArdoqId[];
};

export const saveTagsSuccess = actionCreator<SaveTagsSuccessPayload>(
  '[ComponentOverview2025] SAVE_TAGS_SUCCESS'
);

export type UpdateParentPayload = {
  componentId: ArdoqId;
  oldParentId: ArdoqId | null;
  newParentId: ArdoqId | null;
};
export const updateParent = actionCreator<UpdateParentPayload>(
  '[ComponentOverview2025] UPDATE_PARENT'
);

export const componentParentUpdated = actionCreator(
  '[ComponentOverview2025] COMPONENT_PARENT_UPDATED'
);

export type UpdateComponentTypePayload = {
  componentId: ArdoqId;
  oldTypeId: ArdoqId;
  newTypeId: ArdoqId;
};
export const updateComponentType = actionCreator<UpdateComponentTypePayload>(
  '[ComponentOverview2025] UPDATE_COMPONENT_TYPE'
);

export const componentTypeUpdated = actionCreator(
  '[ComponentOverview2025] COMPONENT_TYPE_UPDATED'
);

export const setFieldsFilterTerm = actionCreator<string>(
  '[ComponentOverview2025] SET_FIELDS_FILTER_TERM'
);

export const setFieldsSortBy = actionCreator<FieldsSortBy>(
  '[ComponentOverview2025] SET_FIELDS_SORT_BY'
);

export const toggleFieldGroupExpanded = actionCreator<string>(
  '[ComponentOverview2025] TOGGLE_FIELD_GROUP_EXPANDED'
);

export const viewpointHighlighted = actionCreator<string>(
  '[ComponentOverview2025] VIEWPOINT_HIGHLIGHTED'
);

export const previewViewpointSelected = actionCreator<ArdoqId>(
  '[ComponentOverview2025] PREVIEW_VIEWPOINT_SELECTED'
);

export const bypassBlockDiagramPreviewDataLimit = actionCreator<void>(
  '[ComponentOverview2025] BYPASS_BLOCK_DIAGRAM_PREVIEW_DATA_LIMIT'
);

export const filterTermChanged = actionCreator<string>(
  '[ComponentOverview2025] FILTER_TERM_CHANGED'
);

export type ShowComponentOverviewDrawerPayload = {
  componentId: string;
  trackingLocation: TrackingLocation;
  startTab?: ComponentOverviewTab;
  onCloseCallback?: VoidFunction;
};

export const showComponentOverviewDrawer = actionCreator<
  Trackable<ShowComponentOverviewDrawerPayload>
>('[ComponentOverviewDrawer] SHOW_COMPONENT_OVERVIEW_DRAWER');

export const closeComponentOverviewDrawer = actionCreator<void>(
  '[ComponentOverviewDrawer] CLOSE_COMPONENT_OVERVIEW_DRAWER'
);
